/**
 * ADR-175 D1 / D8 — the amendable fields' SCOPE, mirrored from radar-api's
 * registry (src/utils/amendableFields.js). The shared fixture
 * (__tests__/fixtures/amendableFieldScopes.json, byte-identical in both
 * repos) keeps the two tables equal.
 *
 *   'contract' — recorded on the one contract the dialog is on.
 *   'person'   — recorded on EVERY contract in force on the effective date
 *                (position, full name): each contract's counterparty sees
 *                the addendum on its own contract.
 *
 * The server decides where an amendment lands; this module only lets the
 * dialog say so before submit.
 */

export const AMENDMENT_SCOPES = Object.freeze({ CONTRACT: 'contract', PERSON: 'person' });

export const AMENDABLE_FIELD_SCOPES = Object.freeze({
  termEndDate: AMENDMENT_SCOPES.CONTRACT,
  contractCompanyId: AMENDMENT_SCOPES.CONTRACT,
  contractTypeId: AMENDMENT_SCOPES.CONTRACT,
  engagementModelId: AMENDMENT_SCOPES.CONTRACT,
  positionId: AMENDMENT_SCOPES.PERSON,
  fullName: AMENDMENT_SCOPES.PERSON
});

export const amendmentScopeOf = (field) => AMENDABLE_FIELD_SCOPES[field] || null;

export const isPersonScopedField = (field) => amendmentScopeOf(field) === AMENDMENT_SCOPES.PERSON;

const ymd = (value) => (value ? String(value).slice(0, 10) : null);

/**
 * The contracts in force on `effectiveYmd`, mirroring radar-api's
 * findContractsInForceOn: open, started by that date, not scheduled to end
 * before it; the primary first. Reads employee.contracts[] (ADR-156 §5).
 *
 * @param {Object[]} contracts - employee.contracts
 * @param {string} effectiveYmd - YYYY-MM-DD
 * @returns {Object[]}
 */
export const contractsInForceOn = (contracts, effectiveYmd) => {
  if (!Array.isArray(contracts) || !effectiveYmd) return [];
  const inForce = contracts.filter((contract) => {
    if (contract.endDate) return false; // closed: no longer amendable
    if (ymd(contract.startDate) && ymd(contract.startDate) > effectiveYmd) return false;
    if (ymd(contract.termEndDate) && ymd(contract.termEndDate) < effectiveYmd) return false;
    return true;
  });
  return [
    ...inForce.filter((contract) => contract.isPrimary),
    ...inForce.filter((contract) => !contract.isPrimary)
  ];
};

/** Mirror of Gate A's episode half: absent source = radar-owned. */
export const isRadarOwnedContract = (contract) => !contract?.source || contract.source === 'radar';

/**
 * ADR-188 D3 — the effective-date bounds of an amendment, mirroring the
 * server's assertEffectiveWithinContract: a CONTRACT fact takes effect while
 * its contract runs (on/after its start, on/before its scheduled end); the
 * default date is today, or the start of a contract that has not started yet.
 * A PERSON fact has no per-contract bound here — its reach (the contracts in
 * force on the date) is what the server checks, and personNotInForce says so.
 *
 * @param {Object} args
 * @param {Object|null} args.contract  the amended contract's contracts[] entry
 * @param {Object[]} args.contracts    employee.contracts (for a person fact)
 * @param {string} args.field          the amended field
 * @param {string|null} args.effectiveYmd
 * @param {string} args.todayYmd
 * @returns {{ minYmd: string|null, maxYmd: string|null, defaultYmd: string, problem: string|null }}
 */
export const amendmentEffectiveBounds = ({ contract, contracts = [], field, effectiveYmd = null, todayYmd }) => {
  const startYmd = ymd(contract?.startDate);
  const endYmd = ymd(contract?.termEndDate);
  const defaultYmd = startYmd && startYmd > todayYmd ? startYmd : todayYmd;

  if (isPersonScopedField(field)) {
    // The server checks reach only when the person holds more than one open
    // contract (findContractsInForceOn returns null otherwise).
    const open = (contracts || []).filter((c) => !c.endDate);
    const notInForce = Boolean(contract && effectiveYmd && open.length > 1
      && !contractsInForceOn(contracts, effectiveYmd).some((c) => c.id === contract.id));
    return {
      minYmd: null,
      maxYmd: null,
      defaultYmd,
      problem: notInForce
        ? `This contract is not in force on ${effectiveYmd.split('-').reverse().join('/')} — record the change from a contract that is, or pick a date it runs on`
        : null
    };
  }

  let problem = null;
  if (effectiveYmd && startYmd && effectiveYmd < startYmd) {
    problem = `The contract starts on ${startYmd.split('-').reverse().join('/')} — the amendment cannot take effect before it`;
  } else if (effectiveYmd && endYmd && effectiveYmd > endYmd) {
    problem = `The contract is scheduled to end on ${endYmd.split('-').reverse().join('/')} — an amendment after it would never take effect`;
  }
  return { minYmd: startYmd, maxYmd: endYmd, defaultYmd, problem };
};
